import axios from 'axios';
import { LEGO_CONSTANTS } from 'constants/index';

const MAX_SAFE_BIGINT = BigInt(Number.MAX_SAFE_INTEGER);
const MIN_SAFE_BIGINT = BigInt(Number.MIN_SAFE_INTEGER);

/** Marks values that were JSON *number* literals rewritten as strings; real JSON strings never use this prefix. */
const BIGINT_SENTINEL_PREFIX = '__BIGINT__';

const isDigit = ch => ch >= '0' && ch <= '9';

/** `intPart` is only candidates when length ≥16 (smaller integers fit in safe Number range). */
function mustQuoteIntegerAsBigInt(intPart) {
  if (!/^-?\d{16,}$/.test(intPart)) {
    return false;
  }
  try {
    const bi = BigInt(intPart);
    return bi > MAX_SAFE_BIGINT || bi < MIN_SAFE_BIGINT;
  } catch {
    return false;
  }
}

/**
 * Read one JSON number literal starting at `start`, return end index and the slice to emit unchanged.
 * Integer tokens that overflow JS safe integers are not valid here — handled by caller.
 */
function consumeJsonNumberPassThrough(json, start, n) {
  let i = start;
  if (json[i] === '-') {
    i++;
    if (i >= n || !isDigit(json[i])) {
      return { end: start + 1, raw: '-' };
    }
  }
  const tokenStart = start;
  while (i < n && isDigit(json[i])) {
    i++;
  }
  if (i < n && json[i] === '.') {
    i++;
    while (i < n && isDigit(json[i])) {
      i++;
    }
  }
  if (i < n && (json[i] === 'e' || json[i] === 'E')) {
    i++;
    if (i < n && (json[i] === '+' || json[i] === '-')) {
      i++;
    }
    while (i < n && isDigit(json[i])) {
      i++;
    }
  }
  return { end: i, raw: json.slice(tokenStart, i) };
}

/**
 * Scan raw JSON text; outside quoted strings, replace integer literals that exceed MAX_SAFE_INTEGER
 * with a quoted sentinel string so JSON.parse + reviver can produce BigInt. Floats and scientific
 * notation are copied unchanged.
 */
function quoteUnsafeIntegerTokens(json) {
  const out = [];
  let i = 0;
  const n = json.length;
  let inString = false;
  let escapeNext = false;

  while (i < n) {
    if (escapeNext) {
      out.push(json[i++]);
      escapeNext = false;
      continue;
    }
    if (inString) {
      const ch = json[i++];
      out.push(ch);
      if (ch === '\\') {
        escapeNext = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    const c = json[i];
    if (c === '"') {
      inString = true;
      out.push(c);
      i++;
      continue;
    }

    if (c === '-' || isDigit(c)) {
      const start = i;
      if (c === '-') {
        i++;
        if (i >= n || !isDigit(json[i])) {
          out.push('-');
          continue;
        }
      }
      const intStart = start;
      while (i < n && isDigit(json[i])) {
        i++;
      }
      const intPart = json.slice(intStart, i);

      if (i < n && (json[i] === '.' || json[i] === 'e' || json[i] === 'E')) {
        const { end, raw } = consumeJsonNumberPassThrough(json, start, n);
        out.push(raw);
        i = end;
        continue;
      }

      if (mustQuoteIntegerAsBigInt(intPart)) {
        out.push(`"${BIGINT_SENTINEL_PREFIX}${intPart}"`);
      } else {
        out.push(intPart);
      }
      continue;
    }

    out.push(c);
    i++;
  }

  return out.join('');
}

/** Reviver: only JSON numbers rewritten with BIGINT_SENTINEL_PREFIX become BigInt. */
function reviveBigIntStrings(_, value) {
  if (typeof value !== 'string' || !value.startsWith(BIGINT_SENTINEL_PREFIX)) {
    return value;
  }
  try {
    return BigInt(value.slice(BIGINT_SENTINEL_PREFIX.length));
  } catch {
    return value;
  }
}

axios.defaults.xsrfCookieName = undefined;
axios.defaults.xsrfHeaderName = undefined;
class AxiosClient {
  constructor() {
    let sessionCSRFToken = null;
    this.client = axios.create({
      timeout: 10000,
      withCredentials: false,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE',
      },
    });

    this.client.interceptors.request.use(
      request => {
        if (sessionCSRFToken) {
          request.headers[LEGO_CONSTANTS.X_XSRF_TOKEN.toUpperCase()] = sessionCSRFToken;
        }
        return request;
      },
      error => Promise.reject(error)
    );

    // Set up transformResponse to handle JSON parsing with precision preservation
    this.client.defaults.transformResponse = [
      function (data) {
        // transformResponse receives the raw response string
        if (typeof data !== 'string') {
          return data;
        }
        try {
          const processedData = quoteUnsafeIntegerTokens(data);
          return JSON.parse(processedData, reviveBigIntStrings);
        } catch (error) {
          return data;
        }
      },
    ];

    this.client.interceptors.response.use(
      response => {
        sessionCSRFToken = response.headers[LEGO_CONSTANTS.X_XSRF_TOKEN];
        return response.data;
      },
      error => Promise.reject(error.response)
    );
  }

  /**
   * Serialize request bodies. Mirrors response parsing: `BigInt` becomes JSON *number* tokens
   * (including integers beyond `Number.MAX_SAFE_INTEGER`), not quoted strings.
   * `JSON.stringify` cannot emit BigInt; we temporarily stringify as `"<digits>n"`, then strip quotes
   * so the wire format is valid JSON with numeric literals.
   */
  toJson(data) {
    if (data === undefined) {
      return undefined;
    }
    const json = JSON.stringify(data, (_, v) => (typeof v === 'bigint' ? `${v}n` : v));
    return json.replace(/"(-?\d+)n"/g, '$1');
  }

  sendRequest(requestConfig) {
    const { url: requestUrl, method: requestMethod, body, headers: formDataHeaders } = requestConfig;
    const url = requestUrl ?? '';
    const method = requestMethod ?? 'GET';
    const formDataRequestHeaders = formDataHeaders?.headers;
    const data = formDataRequestHeaders?.[LEGO_CONSTANTS.CONTENT_TYPE] === LEGO_CONSTANTS.FORMDATA_MULTIPART ? body : this.toJson(body ?? {});
    return this.client.request({
      method,
      url,
      data,
      headers: { ...formDataRequestHeaders },
    });
  }
}

export const axiosClient = new AxiosClient();
